import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import tw from 'tailwind.macro';
import { formatFileSize } from '../../helpers/file';
import { CloseIcon } from '../../images/icons/close';
import { DownloadIcon } from '../../images/icons/download';
import { FileIcon } from '../../images/icons/fileIcon';
import { colors } from '../../styles/constants';
import { Icon12 } from '../Icon';
import { dropzoneMessages } from './messages';
import { FileWithPreview } from './types';

const Container = styled.div<{ isDragActive: boolean; isDisabled: boolean }>`
  ${tw`p-big border-2 border-dashed border-grey-300 cursor-pointer`}
  ${({ isDragActive }) => isDragActive && tw`border-primary-400`}
  ${({ isDisabled }) => isDisabled && tw`opacity-50`}
`;

const Wrapper = styled.div`
  ${tw`flex flex-col items-center justify-center`}
  gap:var(--size-medium)
`;

const IconWrapper = styled.span`
  ${tw`bg-primary-400 rounded-full p-small flex items-center justify-center`}
`;

const TextWrapper = styled.div`
  ${tw`flex flex-col items-center justify-center text-center`}
  gap:var(--size-small)
`;

const Text = styled.span`
  ${tw`text-p-medium font-semibold`}
`;

const SubText = styled.span`
  ${tw`text-p-small`}
`;

type DropzoneProps = {
  setFiles: Dispatch<SetStateAction<FileWithPreview[]>>;
  files: FileWithPreview[];
} & Pick<
  DropzoneOptions,
  'accept' | 'maxFiles' | 'maxSize' | 'disabled' | 'onDropRejected'
>;

export const Dropzone = (props: DropzoneProps) => {
  const {
    accept,
    disabled,
    files,
    maxFiles,
    maxSize,
    onDropRejected,
    setFiles,
  } = props;
  const filesRef = useRef<FileWithPreview[]>(files);
  const isDisabled = disabled || files.length === maxFiles;

  const onDrop: DropzoneOptions['onDrop'] = useCallback(
    (acceptedFiles: File[]) => {
      const filesWithPreview = acceptedFiles.map((file) => {
        let preview = null;

        if (file.type.startsWith('image/')) {
          preview = URL.createObjectURL(file);
        }

        return Object.assign(file, { preview });
      });

      setFiles((prevFiles) => [...prevFiles, ...filesWithPreview]);
    },
    [setFiles],
  );

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    return () => {
      filesRef.current.forEach((file) => {
        if (file.preview) {
          //image preview (object URLs) has to be revoked to prevent memory leaks
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, []);

  const removeFile = (fileIndex: number) => {
    setFiles((currentFiles) => {
      const fileToRemove = currentFiles[fileIndex];

      if (fileToRemove.preview) {
        //image preview (object URLs) has to be revoked to prevent memory leaks
        URL.revokeObjectURL(fileToRemove.preview);
      }

      return currentFiles.filter((_, index) => index !== fileIndex);
    });
  };

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles,
    maxSize,
    disabled: isDisabled,
    onDropRejected,
  });

  return (
    <div>
      <Container
        {...getRootProps()}
        isDisabled={isDisabled}
        isDragActive={isDragActive}
      >
        <input {...getInputProps()} />

        <Wrapper>
          <IconWrapper>
            <DownloadIcon />
          </IconWrapper>

          <TextWrapper>
            <Text>
              <FormattedHTMLMessage {...dropzoneMessages.clickToUpload} />
            </Text>

            {maxSize && (
              <SubText>
                <FormattedMessage
                  {...dropzoneMessages.maxFileSize}
                  values={{
                    maxFileSize: formatFileSize(maxSize),
                  }}
                />
              </SubText>
            )}
          </TextWrapper>
        </Wrapper>
      </Container>

      <UploadedFiles files={files} removeFile={removeFile} />
    </div>
  );
};

const UploadedFilesContainer = styled.div`
  ${tw`mt-medium2 flex flex-col`}
  gap:var(--size-medium)
`;

type UploadedFilesProps = {
  files: FileWithPreview[];
  removeFile: (index: number) => void;
};

const UploadedFiles = (props: UploadedFilesProps) => {
  const { files, removeFile } = props;

  return (
    <UploadedFilesContainer>
      {files.map((file, index) => (
        <File key={index} file={file} onRemove={() => removeFile(index)} />
      ))}
    </UploadedFilesContainer>
  );
};

const FileContainer = styled.div`
  ${tw`p-medium flex border border-solid border-grey-300`}
  gap:var(--size-medium);
  border-radius: 10px;
`;

const FilePreview = styled.div`
  ${tw`border bg-white border-solid rounded-5 border-grey-300 flex items-center justify-center overflow-hidden`}
  width: 50px;
  height: 50px;
`;

const FilePreviewImage = styled.img`
  ${tw`w-full`}
`;

const FileDetails = styled.div`
  ${tw`flex flex-col flex-1 overflow-hidden`}
  gap:var(--size-small)
`;

const FileDetailsText = styled(SubText)`
  ${tw`overflow-hidden`}

  text-overflow: ellipsis;
  white-space: nowrap;
`;

type FileProps = {
  file: FileWithPreview;
  onRemove: () => void;
};

const File = (props: FileProps) => {
  const { file, onRemove } = props;

  return (
    <FileContainer>
      <FilePreview>
        {file.preview ? (
          <FilePreviewImage alt={'Image Preview'} src={file.preview} />
        ) : (
          <FileIcon />
        )}
      </FilePreview>

      <FileDetails>
        <FileDetailsText>{file.name}</FileDetailsText>
        <FileDetailsText style={tw`text-placeHolders`}>
          {formatFileSize(file.size)}
        </FileDetailsText>
      </FileDetails>

      <div>
        <button onClick={onRemove}>
          <CloseIcon
            color={colors.text}
            customStyle={css`
              ${Icon12}
            `}
          />
        </button>
      </div>
    </FileContainer>
  );
};
